import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { 
  Close, 
  PersonOutline, 
  CalendarTodayOutlined
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { COLORS, getStatusColor } from '../../shared/colors';
import { ICONS } from '../../shared/icons';
import CommentBox from './CommentBox';
import { addTaskModalStyles } from '../../shared/globalSX';
import { stringToColor } from '../../shared/commonFunctions';

const BulkEditModal = ({ open, onClose, onSubmit, selectedTasks = [], loading = false, bulkModeColumnId = null, totalCount = 0 }) => {
  const { t } = useTranslation();
  const { dropdowns } = useSelector(state => state.tickets);
  const dialogSx = {
    '& .MuiDialog-container': {
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& .MuiDialog-paper': {
      margin: 0,
      position: 'relative'
    }
  };
  
  const [formData, setFormData] = useState({
    dueDate: null,
    assignee: '',
    status: '',
  });
  const [commentContent, setCommentContent] = useState('');
  
  // Menu anchors
  const [assigneeAnchor, setAssigneeAnchor] = useState(null);
  const [statusAnchor, setStatusAnchor] = useState(null);
  const dateInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFormData({
        dueDate: null,
        assignee: '',
        status: '',
      });
      setCommentContent('');
    }
  }, [open]);

  const handleSubmit = () => {
    const updates = {};
    if (formData.dueDate) updates.dueDate = formData.dueDate;
    if (formData.assignee) updates.assignee = formData.assignee;
    if (formData.status) updates.status = formData.status;
    const trimmedComment = (commentContent || '').trim();
    if (trimmedComment) updates.comments = trimmedComment;
    
    onSubmit(updates);
  };

  const getDisplayName = (value, type) => {
    if (!value) return '';
    if (type === 'assignee') {
      const assignee = dropdowns.assignees?.find(a => a.id === value);
      return assignee?.name || assignee?.username || value;
    }
    if (type === 'status') {
      const status = dropdowns.statuses?.find(s => s.id === value);
      return status?.name || value;
    }
    return value;
  };

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    const initials = parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('');
    return initials || name[0]?.toUpperCase() || '';
  };

  const hasMeaningfulCommentContent = (content) => {
    if (!content) return false;
    if (typeof document === 'undefined') return Boolean(String(content).trim());

    const container = document.createElement('div');
    container.innerHTML = content;
    return Boolean((container.textContent || '').replace(/\u00a0/g, ' ').trim() || container.querySelector('img, video, audio, iframe, canvas, svg, table, pre, code'));
  };

  const hasUpdates = Boolean(
    formData.dueDate
    || formData.assignee
    || formData.status
    || hasMeaningfulCommentContent(commentContent)
  );


  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableScrollLock
      container={typeof document !== 'undefined' ? document.body : undefined}
      sx={dialogSx}
      PaperProps={{
        sx: { borderRadius: '12px', overflow: 'hidden', maxWidth: '750px' }
      }}
    >
      {/* HEADER SECTION */}
      <Box sx={{ p: 2, px: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box component="img" src={ICONS.editBulk} sx={{ width: 32, height: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: COLORS.black, mb: 0.25 }}>
              {t('dashboard.bulkActions.editModal.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.gray[500], fontSize: '15px' }}>
              {t('dashboard.bulkActions.editModal.subtitle', { count: selectedTasks.length, total: totalCount })}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: COLORS.black }}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ mx: 3, height: '1px', bgcolor: COLORS.ui.divider }} />

      <DialogContent sx={{ p: 2.5, px: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* SELECTED TASKS RIBBON */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 1.25, 
          px: 2, 
          bgcolor: COLORS.ui.ghostBg, 
          border: `1px solid ${COLORS.ui.ghostBorder}`,
          borderRadius: '4px'
        }}>
          <Typography sx={{ fontWeight: 500, color: COLORS.gray[700], fontSize: '14px', whiteSpace: 'nowrap' }}>
            {t('dashboard.bulkActions.editModal.selectedTasks')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
            {selectedTasks.map(task => (
              <Chip
                key={task.id}
                label={task.ticket_number || task.title}
                size="small"
                sx={{ 
                  bgcolor: COLORS.ui.chipBg, 
                  color: COLORS.ui.chipText, 
                  border: `1px solid ${COLORS.ui.ghostBorder}`, 
                  fontWeight: 500, 
                  borderRadius: '4px',
                  fontSize: '11px',
                  height: 24
                }}
              />
            ))}
          </Box>
        </Box>

        {/* COMMENT BOX SECTION */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box component="img" src={ICONS.bulkEditComment} sx={{ width: 20, height: 20 }} />
            <Typography sx={{ fontSize: '20px', fontWeight: 500, color: COLORS.gray[500] }}>
              {t('dashboard.bulkActions.editModal.addComment')}
            </Typography>
          </Box>
          <Box sx={{ 
            border: `1px solid ${COLORS.gray[100]}`, 
            borderRadius: '8px', 
            overflow: 'hidden',
            minHeight: '100px'
          }}>
            <CommentBox
              onSubmit={(content) => setCommentContent(content)}
              onContentChange={(content) => setCommentContent(content)}
              hideAttachments={true}
              hideActions={true}
              placeholder=""
            />
          </Box>
        </Box>

        {/* FIELD SELECTORS ROW */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {/* Status Button */}
          <Button
            variant="outlined"
            onClick={(e) => setStatusAnchor(e.currentTarget)}
            startIcon={!formData.status ? <img src={ICONS.status} alt="status" style={{ width: 18, height: 18 }} /> : null}
            sx={{ ...addTaskModalStyles.fieldButton, flex: 1, position: 'relative' }}
          >
            {formData.status && (
              <Box sx={{
                position: 'absolute',
                top: '-10px',
                left: '12px',
                bgcolor: COLORS.white,
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <img src={ICONS.status} alt="status" style={{ width: 14, height: 14, opacity: 0.6 }} />
                <Typography sx={{
                  fontSize: '13px',
                  color: COLORS.gray[500],
                  fontWeight: 500,
                  lineHeight: 1
                }}>
                  {t('dashboard.bulkActions.editModal.status')}
                </Typography>
              </Box>
            )}
            {formData.status ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(getDisplayName(formData.status, 'status')), flexShrink: 0 }} />
                <Typography variant="body2" sx={{ 
                  fontSize: '13px', 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  textAlign: 'left'
                }}>
                  {getDisplayName(formData.status, 'status')}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ 
                fontSize: '13px', 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                textAlign: 'left'
              }}>
                {t('dashboard.bulkActions.editModal.status')}
              </Typography>
            )}
          </Button>

          {/* Assignee Button */}
          <Button
            variant="outlined"
            onClick={(e) => setAssigneeAnchor(e.currentTarget)}
            startIcon={!formData.assignee ? <PersonOutline sx={{ fontSize: 18 }} /> : null}
            sx={{ ...addTaskModalStyles.fieldButton, flex: 1, position: 'relative' }}
          >
            {formData.assignee && (
              <Box sx={{
                position: 'absolute',
                top: '-10px',
                left: '12px',
                bgcolor: COLORS.white,
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <PersonOutline sx={{ fontSize: 14, color: COLORS.gray[500] }} />
                <Typography sx={{
                  fontSize: '13px',
                  color: COLORS.gray[500],
                  fontWeight: 500,
                  lineHeight: 1
                }}>
                  {t('dashboard.bulkActions.editModal.assignee')}
                </Typography>
              </Box>
            )}
            {formData.assignee ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                <Avatar sx={{ width: 20, height: 20, fontSize: '9px', bgcolor: stringToColor(getDisplayName(formData.assignee, 'assignee')) }}>
                  {getInitials(getDisplayName(formData.assignee, 'assignee'))}
                </Avatar>
                <Typography variant="body2" sx={{ 
                  fontSize: '13px', 
                  whiteSpace: 'nowrap', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  textAlign: 'left'
                }}>
                  {getDisplayName(formData.assignee, 'assignee')}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ 
                fontSize: '13px', 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                textAlign: 'left'
              }}>
                {t('dashboard.bulkActions.editModal.assignee')}
              </Typography>
            )}
          </Button>

          {/* Due Date Button */}
          <Button
            variant="outlined"
            onClick={() => dateInputRef.current?.showPicker()}
            startIcon={!formData.dueDate ? <CalendarTodayOutlined sx={{ fontSize: 18 }} /> : null}
            sx={{ ...addTaskModalStyles.fieldButton, flex: 1, position: 'relative' }}
          >
            {formData.dueDate && (
              <Box sx={{
                position: 'absolute',
                top: '-10px',
                left: '12px',
                bgcolor: COLORS.white,
                px: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <CalendarTodayOutlined sx={{ fontSize: 14, color: COLORS.gray[500] }} />
                <Typography sx={{
                  fontSize: '13px',
                  color: COLORS.gray[500],
                  fontWeight: 500,
                  lineHeight: 1
                }}>
                  {t('dashboard.bulkActions.editModal.dueDate')}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" sx={{ 
              fontSize: '13px', 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              textAlign: 'left'
            }}>
              {formData.dueDate || t('dashboard.bulkActions.editModal.dueDate')}
            </Typography>
            <input
              type="date"
              ref={dateInputRef}
              value={formData.dueDate || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                border: 0
              }}
            />
          </Button>
        </Box>


      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3, pt: 0, justifyContent: 'flex-end' }}>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading || !hasUpdates}
          sx={{ 
            bgcolor: COLORS.primary, 
            color: COLORS.white,
            textTransform: 'none',
            fontSize: '18px',
            fontWeight: 600,
            px: 4,
            py: 1.2,
            borderRadius: '10px',
            boxShadow: '0 4px 6px rgba(59, 130, 246, 0.25)',
            '&:hover': { 
              bgcolor: COLORS.blue[600],
              boxShadow: '0 6px 12px rgba(59, 130, 246, 0.35)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              transform: 'translateY(0)'
            },
            '&.Mui-disabled': { 
              bgcolor: COLORS.gray[200], 
              color: COLORS.gray[400],
              boxShadow: 'none'
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {t('dashboard.bulkActions.editModal.updateButton', { count: selectedTasks.length, total: totalCount })}
        </Button>
      </DialogActions>

      {/* MENUS */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        PaperProps={{ style: { maxHeight: 250 } }}
      >
        {dropdowns.statuses?.map(status => (
          <MenuItem 
            key={status.id} 
            onClick={() => {
              setFormData(prev => ({ ...prev, status: status.id }));
              setStatusAnchor(null);
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusColor(status.name), flexShrink: 0 }} />
            <Typography sx={{ fontSize: '13px' }}>{status.name}</Typography>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={assigneeAnchor}
        open={Boolean(assigneeAnchor)}
        onClose={() => setAssigneeAnchor(null)}
      >
        {dropdowns.assignees?.map(assignee => (
          <MenuItem key={assignee.id} onClick={() => {
            setFormData(prev => ({ ...prev, assignee: assignee.id }));
            setAssigneeAnchor(null);
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, fontSize: '10px', bgcolor: stringToColor(assignee.name) }}>
                {getInitials(assignee.name)}
              </Avatar>
              <Typography sx={{ fontSize: '13px' }}>{assignee.name}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

    </Dialog>
  );
};

export default BulkEditModal;
